import {useCallback, useEffect, useRef} from 'react'

interface UseInfiniteScrollOptions {
  hasMore: boolean
  next: () => void
  scrollThreshold?: number
  rootId?: string
}

interface UseInfiniteScrollReturn {
  sentinelRef: (node: HTMLElement | null) => void
}

export const useInfiniteScroll = ({
  hasMore,
  next,
  scrollThreshold = 0.9,
  rootId,
}: UseInfiniteScrollOptions): UseInfiniteScrollReturn => {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const nodeRef = useRef<HTMLElement | null>(null)
  const nextRef = useRef(next)
  const hasMoreRef = useRef(hasMore)

  nextRef.current = next
  hasMoreRef.current = hasMore

  const observe = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    const node = nodeRef.current
    if (!node) return

    const root = rootId ? document.getElementById(rootId) : null

    observerRef.current = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting && hasMoreRef.current) {
          nextRef.current()
        }
      },
      {
        root,
        rootMargin: `${Math.round((1 - scrollThreshold) * 100)}%`,
        threshold: 0,
      },
    )

    observerRef.current.observe(node)
  }, [rootId, scrollThreshold])

  const sentinelRef = useCallback(
    (node: HTMLElement | null) => {
      nodeRef.current = node
      observe()
    },
    [observe],
  )

  useEffect(() => {
    if (hasMore) {
      observe()
    }
  }, [hasMore, observe])

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect()
    }
  }, [])

  return {sentinelRef}
}
